import { Box, Button, Flex, Grid, GridItem, HStack, Icon, Text, VStack } from '@chakra-ui/react'
import { faCalendar, faChevronDown, faPaperPlane, faTag, faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import type { PolicyVersionItem, PremiumDelta } from '@/dtos/exceptionalDiscount'
import { PolicyVersionSource, PolicyVersionStatus } from '@/dtos/exceptionalDiscount'
import { formatDate, formatDateTime } from '@/utils/formatDate'
import { DiffChip } from '../components/DiffChip'
import { StatusChip } from '../components/StatusChip'
import { fmtKc } from '../lib/premiumMath'
import { RizikoRow } from './RizikoRow'
import { versionGridTemplate } from './versionGrid'
import { isDiscountableRisk } from '../lib/risks'

const originVisuals: Record<PolicyVersionSource, { label: string, icon: typeof faTag }> = {
  [PolicyVersionSource.createContract]: { label: 'Sjednání smlouvy', icon: faPaperPlane },
  [PolicyVersionSource.indexation]: { label: 'Výroční indexace', icon: faCalendar },
  [PolicyVersionSource.exceptionalDiscount]: { label: 'Mimořádná sleva', icon: faTag },
}

const GroupHeader = ({ children, colSpan }: { children: string, colSpan?: number }) => (
  <GridItem colSpan={colSpan} textAlign="center" py={2} borderBottomWidth="1px" borderColor="white">
    <Text fontSize="sm" fontWeight="bold" letterSpacing="0.1em" textTransform="uppercase" color="primary.700">
      {children}
    </Text>
  </GridItem>
)

const SubHeader = ({ children, align }: { children: string, align?: 'left' | 'right' }) => (
  <Text
    fontSize="2xs"
    fontWeight="bold"
    letterSpacing="0.04em"
    textTransform="uppercase"
    color="gray.500"
    textAlign={align ?? 'right'}
    px={4}
    py={2}
    borderBottomWidth="1px"
    borderColor="white"
  >
    {children}
  </Text>
)

const TotalCell = ({ value, delta }: { value?: number, delta?: PremiumDelta | null }) => (
  <VStack alignItems="flex-end" spacing={1} px={4} py={2}>
    <Text fontWeight="bold" fontSize="lg">{value !== undefined && value !== null ? fmtKc(value) : '—'}</Text>
    <DiffChip delta={delta} />
  </VStack>
)

export const VerzeCard = ({ version, expanded, onToggleExpand, showDelete, onDelete }: {
  version: PolicyVersionItem
  expanded: boolean
  onToggleExpand: () => void
  showDelete: boolean
  onDelete: () => void
}) => {
  const origin = originVisuals[version.source]
  const isPast = version.status === PolicyVersionStatus.terminated
  const mains = version.risks.filter(risk => isDiscountableRisk(risk.riskType))
  const addons = version.risks.filter(risk => !isDiscountableRisk(risk.riskType))

  return (
    <VStack alignItems="stretch" spacing={2} flex={1} minW={0} opacity={isPast ? 0.75 : 1}>
      <Flex alignItems="center" gap={2.5} flexWrap="wrap" minH={6}>
        <Text fontWeight="bold" fontSize="md" whiteSpace="nowrap">
          {formatDate(new Date(version.validFrom))}
          <Text as="span" fontWeight="normal" color="gray.400" mx={1}>–</Text>
          {formatDate(new Date(version.validTo))}
        </Text>
        <Text color="gray.400">·</Text>
        <StatusChip status={version.status} />
        <Text color="gray.400">·</Text>
        <HStack spacing={1.5}>
          <Icon as={FontAwesomeIcon} icon={origin.icon} boxSize={3.5} color="gray.500" />
          <Text fontSize="sm" color="gray.500">{origin.label}</Text>
        </HStack>
        {showDelete && (
          <>
            <Text color="gray.400">·</Text>
            <Button
              size="xs"
              variant="outline"
              colorScheme="red"
              leftIcon={<Icon as={FontAwesomeIcon} icon={faTrashCan} />}
              onClick={onDelete}
              title="Odstranit čekající verzi s mimořádnou slevou"
            >
              Odstranit
            </Button>
          </>
        )}
        <Text fontSize="xs" color="gray.500" ml="auto" whiteSpace="nowrap">
          {version.createdBy}
          {version.dateCreated && ` · ${formatDateTime(new Date(version.dateCreated))}`}
        </Text>
      </Flex>

      <Grid templateColumns={versionGridTemplate} bg="gray.50" borderRadius="md">
        <GroupHeader>Rizika</GroupHeader>
        <GroupHeader colSpan={2}>Klient</GroupHeader>
        <GroupHeader colSpan={2}>VWFS</GroupHeader>
        <SubHeader align="left">Název</SubHeader>
        <SubHeader>Splátka</SubHeader>
        <SubHeader>Celkem</SubHeader>
        <SubHeader>Splátka</SubHeader>
        <SubHeader>Celkem</SubHeader>
        <Flex alignItems="center" px={4} py={2}>
          <Text fontWeight="bold" fontSize="sm" color="primary.700">Celkem za všechny rizika</Text>
        </Flex>
        <TotalCell value={version.premiumFrequencyClient} delta={version.periodClientDelta} />
        <TotalCell value={version.annualPremiumClient} delta={version.annualClientDelta} />
        <TotalCell value={version.premiumFrequencyVwfs} delta={version.periodVwfsDelta} />
        <TotalCell value={version.annualPremiumVwfs} delta={version.annualVwfsDelta} />
      </Grid>

      {version.risks.length > 0 && (
        <Flex alignItems="center" justifyContent="center" position="relative" py={1} cursor="pointer" onClick={onToggleExpand}>
          <Box position="absolute" left={0} right={0} top="50%" h="1px" bg="gray.100" />
          <HStack
            spacing={1.5}
            px={3}
            py={1}
            bg="white"
            borderRadius="full"
            position="relative"
            color="gray.500"
          >
            <Text fontSize="xs">{expanded ? 'Sbalit rozpad rizik' : 'Zobrazit rozpad rizik'}</Text>
            <Icon
              as={FontAwesomeIcon}
              icon={faChevronDown}
              boxSize={3}
              transform={expanded ? 'rotate(180deg)' : undefined}
              transition="transform .15s ease"
            />
          </HStack>
        </Flex>
      )}

      {expanded && (
        <VStack alignItems="stretch" spacing={0} borderRadius="md" overflow="hidden">
          {mains.map(risk => <RizikoRow key={risk.riskType} risk={risk} />)}
          {addons.map(risk => <RizikoRow key={risk.riskType} risk={risk} muted />)}
        </VStack>
      )}
    </VStack>
  )
}
